'use client'

// ============================================================================
// БЛОКИРОВКА ПРОКРУТКИ СТРАНИЦЫ ПОД ОКНОМ.
// ----------------------------------------------------------------------------
// Пока открыто окно, страница под ним не должна ехать: иначе после закрытия
// человек оказывается не там, где был.
//
// ── ПОЧЕМУ СЧЁТЧИК, А НЕ ПРОСТО ФЛАГ ───────────────────────────────────────
// Каждое окно раньше делало так:
//
//     const prev = document.body.style.overflow
//     document.body.style.overflow = 'hidden'
//     return () => { document.body.style.overflow = prev }
//
// Поодиночке это работает. Но открой два окна подряд — второе запомнит
// `hidden`, оставленный первым, и при закрытии ВЕРНЁТ его. Прокрутка остаётся
// заблокированной, страница «замирает», и лечится только обновлением.
//
// Владелец поймал это на «Ролях и правах»: там окна нет вовсе, а страница не
// прокручивалась — блокировку оставило окно, закрытое до перехода.
//
// Счётчик считает, сколько окон открыто СЕЙЧАС. Снимаем блокировку, когда
// закрылось последнее, — и неважно, в каком порядке они открывались.
// ============================================================================

import { useEffect } from 'react'
import { releaseModalStack } from '@/lib/ui/modal-stack'

let openCount = 0
/** Каким `overflow` был до первого окна. Возвращаем его, когда закрылось последнее. */
let savedOverflow: string | null = null

function lock() {
  if (typeof document === 'undefined') return
  if (openCount === 0) {
    savedOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }
  openCount += 1
}

function unlock() {
  if (typeof document === 'undefined') return
  openCount = Math.max(openCount - 1, 0)
  if (openCount === 0) {
    document.body.style.overflow = savedOverflow ?? ''
    savedOverflow = null
  }
}

/**
 * Заблокировать прокрутку, пока `active`.
 *
 * @param active окно открыто
 */
export function useScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return
    lock()
    return unlock
  }, [active])
}

/**
 * Аварийное снятие: возвращает прокрутку, сколько бы окон ни считалось
 * открытыми.
 *
 * Нужно на смене страницы: окно может размонтироваться без своего обработчика —
 * например, при переходе по ссылке изнутри. Тогда счётчик остаётся ненулевым, и
 * страница замирает навсегда.
 */
export function releaseScrollLock() {
  if (typeof document === 'undefined') return
  openCount = 0
  savedOverflow = null
  document.body.style.overflow = ''
  // Стопка окон страдает от того же: пропуск, оставшийся в ней после ухода со
  // страницы, делает «верхним» окно, которого уже нет, — и Escape перестаёт
  // работать у настоящего. Освобождаем обе разом, в одном месте.
  releaseModalStack()
}
